import { Fragment, useState } from 'react'

import type { CotizacionWorldOffice, SituacionCotizacion } from '@/lib/supabase/tipos-bd'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'
import { SinDatos } from '@/shared/ui/Estados/SinDatos'
import { Paginacion } from '@/shared/ui/Paginacion/Paginacion'
import { Pastilla, type TonoPastilla } from '@/shared/ui/Pastilla/Pastilla'
import { ThOrdenable, type Orden } from '@/shared/ui/Tabla/OrdenTabla'
import { Tarjeta } from '@/shared/ui/Tarjeta/Tarjeta'
import { fecha, fechaHora, pesos, porcentaje } from '@/shared/utilidades/formato'

import { Cotizador } from '@/modules/cotizador/Cotizador'
import { CotizadorLibre } from '@/modules/cotizador/CotizadorLibre'
import { Boton } from '@/shared/ui/Boton/Boton'

import { useSesion } from '@/modules/auth/useSesion'

import { ConceptosCotizacion } from './ConceptosCotizacion'
import { HojaDeLaCotizacion } from './hoja/HojaDeLaCotizacion'
import {
  FILTROS_INICIALES,
  useCotizacionesWorldOffice,
  useAdoptarCotizacion,
  useResumenCotizaciones,
  type CampoCotizaciones,
  type FiltrosCotizaciones,
} from './useCotizaciones'
import estilos from './PaginaCotizaciones.module.css'

/**
 * QUÉ SE PUEDE HACER CON ESTA COTIZACIÓN, Y SI NO SE PUEDE, POR QUÉ.
 *
 * La ficha de la orden ofrece Modificar cuando la cotización está «cotizada»:
 * hasta la aprobación se puede ajustar, después no, porque ya nació la orden
 * de trabajo con ese precio. Esta lista sigue la misma regla —una regla que
 * cambia según dónde se mire no es una regla— y, cuando no se puede, lo dice.
 * Un sitio donde no hay ningún botón y tampoco una explicación parece roto.
 */
function queSePuedeHacer(
  c: CotizacionWorldOffice,
): { modificar: true } | { modificar: false; porque: string | null } {
  if (c.anulado) return { modificar: false, porque: null }

  // Sin cotización nuestra detrás se adopta al vuelo y se modifica igual
  // (0163). Antes esto era un «no» y la diferencia no se veía: unas dejaban y
  // otras no según dónde hubieran nacido.
  if (c.cotizacion_id === null) return { modificar: true }

  if (c.cotizacion_estado === 'cotizada') return { modificar: true }

  if (c.cotizacion_estado === 'aprobada' || c.cotizacion_estado === 'enviada') {
    return {
      modificar: false,
      porque:
        'Ya está aprobada y la orden de trabajo nació con este precio. Lo que aparezca de más va como cotización adicional, desde la orden.',
    }
  }

  if (c.cotizacion_estado === 'rechazada') {
    return {
      modificar: false,
      porque: 'El cliente la rechazó. Se responde con una cotización nueva, no cambiando la que él ya vio.',
    }
  }

  return { modificar: false, porque: null }
}

/** Cómo se llama y se ve cada situación. */
const SITUACIONES: Record<SituacionCotizacion, { etiqueta: string; tono: TonoPastilla }> = {
  libre: { etiqueta: 'Sin enlazar', tono: 'neutro' },
  enlazada: { etiqueta: 'En una orden', tono: 'marca' },
  aprobada: { etiqueta: 'Aprobada', tono: 'cerrado' },
  rechazada: { etiqueta: 'Rechazada', tono: 'detenido' },
  anulada: { etiqueta: 'Anulada', tono: 'detenido' },
}

/**
 * Sección Cotizaciones · Comercial y Clientes.
 *
 * Todo lo que llega de World Office, con su situación. Responde sin abrir el
 * Excel la pregunta de si una cotización ya quedó en alguna orden, y se abre
 * por conceptos como el papel.
 */
export function PaginaCotizaciones() {
  const { puede } = useSesion()
  // Enlazar es crear el puente entre World Office y una orden: mismo permiso
  // que crear la cotización, porque es la misma decisión.
  const puedeEditar = puede('cotizaciones', 'editar')

  /*
   * La que se está modificando: la fila para los datos de cabecera y el id de
   * NUESTRA cotización, que puede acabar de nacer —si el documento se adoptó
   * al pulsar— y por eso no se puede leer de la fila, que ya venía cargada.
   */
  const [modificando, setModificando] = useState<{
    fila: CotizacionWorldOffice
    cotizacionId: string
  } | null>(null)

  const adoptar = useAdoptarCotizacion()

  /** Abre el cotizador sobre un documento, adoptándolo antes si hace falta. */
  async function modificar(c: CotizacionWorldOffice) {
    const id = c.cotizacion_id ?? (await adoptar.mutateAsync(c.id_asiento))
    setModificando({ fila: c, cotizacionId: id })
  }

  /** La que se está viendo como documento, lista para imprimir o guardar. */
  const [enHoja, setEnHoja] = useState<CotizacionWorldOffice | null>(null)

  const [filtros, setFiltros] = useState(FILTROS_INICIALES)
  const [abierta, setAbierta] = useState<number | null>(null)
  const [cotizando, setCotizando] = useState(false)
  const [pagina, setPagina] = useState(0)
  // Lo más reciente primero: la cotización que interesa casi siempre es de
  // esta semana.
  const [orden, setOrden] = useState<Orden<CampoCotizaciones>>({
    campo: 'fecha',
    ascendente: false,
  })

  const { cotizaciones, total, porPagina, cargando, error } = useCotizacionesWorldOffice(
    filtros,
    orden,
    pagina,
  )
  const { resumen } = useResumenCotizaciones()

  /**
   * Filtrar vuelve al principio y cierra lo abierto.
   *
   * Lo abierto podría no estar ya en el resultado. Y el reinicio va en el
   * mismo gesto que el filtro, no en un efecto: con el efecto se cuela una
   * consulta que pide la página vieja sobre el resultado nuevo, y el servidor
   * responde 416.
   */
  function filtrar(cambio: (previos: FiltrosCotizaciones) => FiltrosCotizaciones) {
    setFiltros(cambio)
    setPagina(0)
    setAbierta(null)
  }

  function ordenar(nuevo: Orden<CampoCotizaciones>) {
    setOrden(nuevo)
    setPagina(0)
    setAbierta(null)
  }

  function alternar(idAsiento: number) {
    setAbierta((previa) => (previa === idAsiento ? null : idAsiento))
  }

  function situacionDe(cotizacion: CotizacionWorldOffice) {
    const s = SITUACIONES[cotizacion.situacion]

    // Cuando cuelga de una orden, el número de la orden dice más que la
    // palabra «enlazada»: es lo que la persona necesita para ir a buscarla.
    const etiqueta =
      cotizacion.orden_trabajo_numero ?? cotizacion.orden_numero ?? s.etiqueta

    return (
      <Pastilla tono={s.tono} titulo={s.etiqueta}>
        {etiqueta}
      </Pastilla>
    )
  }

  return (
    <>
      {resumen && (
        <div className={estilos.resumen}>
          <div className={estilos.dato}>
            <p className={estilos.valor}>{resumen.total.toLocaleString('es-CO')}</p>
            <p className={estilos.rotulo}>En World Office</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>{resumen.libres.toLocaleString('es-CO')}</p>
            <p className={estilos.rotulo}>Sin enlazar</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>{resumen.enlazadas + resumen.aprobadas}</p>
            <p className={estilos.rotulo}>En una orden</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>{resumen.libres_sin_cliente.toLocaleString('es-CO')}</p>
            <p className={estilos.rotulo}>Sin cliente</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>{resumen.de_la_semana}</p>
            <p className={estilos.rotulo}>De esta semana</p>
          </div>
          <div className={estilos.dato}>
            <p className={estilos.valor}>
              {resumen.ultima_sincronizacion
                ? fechaHora(resumen.ultima_sincronizacion).split(', ')[1]
                : '—'}
            </p>
            <p className={estilos.rotulo}>Última sincronización</p>
          </div>
        </div>
      )}

      <Tarjeta
        titulo="Cotizaciones de World Office"
        subtitulo="Toque una para ver sus conceptos."
        sinRelleno
        pie={
          <Paginacion pagina={pagina} porPagina={porPagina} total={total} onCambiar={setPagina} />
        }
        acciones={
          <div className={estilos.filtros}>
            <input
              className={estilos.buscador}
              type="search"
              placeholder="Número o cliente…"
              value={filtros.busqueda}
              onChange={(e) => filtrar((p) => ({ ...p, busqueda: e.target.value }))}
              aria-label="Buscar cotización"
            />

            <select
              className={estilos.selector}
              value={filtros.situacion}
              onChange={(e) =>
                filtrar((p) => ({
                  ...p,
                  situacion: e.target.value as SituacionCotizacion | 'todas',
                }))
              }
              aria-label="Situación"
            >
              <option value="todas">Todas</option>
              <option value="libre">Sin enlazar</option>
              <option value="enlazada">En una orden</option>
              <option value="aprobada">Aprobadas</option>
              <option value="anulada">Anuladas</option>
            </select>

            <select
              className={estilos.selector}
              value={filtros.dias}
              onChange={(e) => filtrar((p) => ({ ...p, dias: Number(e.target.value) }))}
              aria-label="Periodo"
            >
              <option value={7}>Últimos 7 días</option>
              <option value={30}>Últimos 30 días</option>
              <option value={90}>Últimos 90 días</option>
              <option value={0}>Todo</option>
            </select>

            <label className={estilos.casilla}>
              <input
                type="checkbox"
                checked={filtros.soloSinCliente}
                onChange={(e) => filtrar((p) => ({ ...p, soloSinCliente: e.target.checked }))}
              />
              Solo sin cliente
            </label>

            {/* Cotizar sin orden de servicio: venta de materiales. Las de un
                trabajo se arman desde su orden, que es donde está el cliente
                y la descripción de lo que hay que hacer. */}
            <Boton variante="principal" onClick={() => setCotizando(true)}>
              Nueva cotización
            </Boton>
          </div>
        }
      >
        {cargando ? (
          <Cargando mensaje="CALIBRANDO MATRIZ COTIZACIONES..." />
        ) : error ? (
          <div style={{ padding: 'var(--e-5)' }}>
            <Aviso tipo="error" titulo="No se pudieron cargar">
              {error}
            </Aviso>
          </div>
        ) : cotizaciones.length === 0 ? (
          <SinDatos
            titulo="Ninguna cotización con esos filtros"
            mensaje="Pruebe ampliando el periodo o quitando la búsqueda."
          />
        ) : (
          <div className={estilos.desplazable}>
            <table className={`${estilos.tabla} ${abierta !== null ? estilos.conAbierta : ''}`}>
              <thead>
                <tr>
                  <ThOrdenable
                    campo="numero"
                    orden={orden}
                    onCambiar={ordenar}
                    ancho={112}
                    primeroDescendente
                  >
                    Cotización
                  </ThOrdenable>

                  <ThOrdenable
                    campo="fecha"
                    orden={orden}
                    onCambiar={ordenar}
                    ancho={104}
                    primeroDescendente
                  >
                    Fecha
                  </ThOrdenable>

                  <ThOrdenable campo="cliente_nombre" orden={orden} onCambiar={ordenar}>
                    Cliente
                  </ThOrdenable>

                  <ThOrdenable
                    campo="total_con_iva"
                    orden={orden}
                    onCambiar={ordenar}
                    derecha
                    ancho={128}
                    primeroDescendente
                  >
                    Total
                  </ThOrdenable>

                  <ThOrdenable
                    campo="conceptos"
                    orden={orden}
                    onCambiar={ordenar}
                    derecha
                    ancho={84}
                    primeroDescendente
                  >
                    Ítems
                  </ThOrdenable>

                  <th style={{ width: 118 }}>Mano de obra</th>

                  <ThOrdenable campo="situacion" orden={orden} onCambiar={ordenar} ancho={152}>
                    Situación
                  </ThOrdenable>
                </tr>
              </thead>

              <tbody>
                {cotizaciones.map((cotizacion) => {
                  const estaAbierta = abierta === cotizacion.id_asiento

                  return (
                    <Fragment key={cotizacion.id_asiento}>
                      <tr
                        className={`${estilos.fila} ${estaAbierta ? estilos.filaAbierta : ''}`}
                        onClick={() => alternar(cotizacion.id_asiento)}
                        tabIndex={0}
                        role="button"
                        aria-expanded={estaAbierta}
                        onKeyDown={(evento) => {
                          if (evento.key === 'Enter' || evento.key === ' ') {
                            evento.preventDefault()
                            alternar(cotizacion.id_asiento)
                          }
                        }}
                      >
                        <td className={estilos.numero}>{cotizacion.numero_visible}</td>
                        <td className={estilos.apagado}>{fecha(cotizacion.fecha)}</td>
                        <td className={cotizacion.es_consumidor_final ? estilos.apagado : undefined}>
                          {cotizacion.cliente_nombre ?? '—'}
                        </td>
                        <td className={`${estilos.derecha} ${estilos.cifra}`}>
                          {pesos(cotizacion.total_con_iva)}
                        </td>
                        <td className={`${estilos.derecha} ${estilos.apagado}`}>
                          {cotizacion.conceptos}
                        </td>
                        <td
                          className={
                            cotizacion.discrimina_mano_de_obra ? estilos.conMano : estilos.apagado
                          }
                        >
                          {cotizacion.porcentaje_mano_de_obra !== null
                            ? porcentaje(cotizacion.porcentaje_mano_de_obra)
                            : 'No discrimina'}
                        </td>
                        <td>{situacionDe(cotizacion)}</td>
                      </tr>

                      {estaAbierta && (
                        <tr className={estilos.filaDetalle}>
                          <td colSpan={7}>
                            {/* LO QUE SE PUEDE HACER, EN EL ENCABEZADO DEL
                                DOCUMENTO. Hasta ahora esta pantalla solo
                                dejaba mirar: quien venía a buscar una
                                cotización por su número tenía que acordarse de
                                qué orden era, ir a la matriz y abrirla para
                                poder cambiarle algo. */}
                            <ConceptosCotizacion
                              cotizacion={cotizacion}
                              acciones={(() => {
                                const puede = queSePuedeHacer(cotizacion)

                                /* VER EL DOCUMENTO SE PUEDE SIEMPRE. Es lo
                                   que el cliente recibe y lo que bodega lee
                                   para alistar: no depende de si la
                                   cotización todavía admite cambios. */
                                const hoja = (
                                  <Boton variante="secundario" onClick={() => setEnHoja(cotizacion)}>
                                    Ver e imprimir
                                  </Boton>
                                )

                                if (puede.modificar) {
                                  return (
                                    <>
                                      <span className={estilos.parAcciones}>
                                        {hoja}
                                        {puedeEditar && (
                                          <Boton
                                            variante="principal"
                                            onClick={() => void modificar(cotizacion)}
                                            trabajando={adoptar.isPending}
                                          >
                                            Modificar
                                          </Boton>
                                        )}
                                      </span>
                                      {puedeEditar && (
                                        <span className={estilos.razon}>
                                          Hasta que gerencia la apruebe.
                                        </span>
                                      )}
                                    </>
                                  )
                                }

                                return (
                                  <>
                                    {hoja}
                                    {puede.porque && (
                                      <span className={estilos.razon}>{puede.porque}</span>
                                    )}
                                  </>
                                )
                              })()}
                            />

                            {/* EL ENLACE SE HACE DESDE LA ORDEN, NO DESDE AQUÍ.
                                Aquí estuvo, y Johan pidió quitarlo: esta
                                pantalla es para mirar el documento, y un
                                buscador de órdenes al pie de los conceptos se
                                lee como un cuadro suelto. La ficha de la orden
                                tiene su «Enlazar cotización», que además es
                                donde se sabe qué trabajo se está mirando. */}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      <CotizadorLibre abierto={cotizando} onCerrar={() => setCotizando(false)} />

      {enHoja && <HojaDeLaCotizacion cotizacion={enHoja} onCerrar={() => setEnHoja(null)} />}

      {/* SE ABRE EL MISMO COTIZADOR DE LA FICHA DE LA ORDEN, no una versión
          de esta pantalla. Dos cotizadores que se parecen acaban siendo dos
          cotizadores distintos.

          Una cotización sin orden es una venta de materiales: va marcada como
          libre para que no le exija mano de obra, que en un mostrador no
          existe. */}
      {modificando && (
        <Cotizador
          abierto
          ordenServicioId={modificando.fila.orden_servicio_id ?? ''}
          ordenNumero={modificando.fila.orden_numero ?? 'Venta de materiales'}
          clienteNombre={
            modificando.fila.cliente_en_la_matriz ?? modificando.fila.cliente_nombre ?? 'Sin cliente'
          }
          onCerrar={() => setModificando(null)}
          modificar={{
            cotizacionId: modificando.cotizacionId,
            numero: modificando.fila.numero_visible,
          }}
          libre={
            modificando.fila.orden_servicio_id === null
              ? { cotizacionId: modificando.cotizacionId }
              : null
          }
        />
      )}
    </>
  )
}
